// Iconos de toda la app: mismos nombres que @mui/icons-material, pero dibujados con
// Lucide (trazo fino y redondeado). Se envuelven en SvgIcon para que fontSize, color,
// sx y los componentes de MUI (IconButton, Chip, ListItemIcon...) sigan funcionando igual.
import { forwardRef } from "react";
import SvgIcon from "@mui/material/SvgIcon";
import {
  Activity as LActivity,
  ArrowLeft as LArrowLeft,
  ArrowRight as LArrowRight,
  AtSign as LAtSign,
  BadgeCheck as LBadgeCheck,
  Bell as LBell,
  BellPlus as LBellPlus,
  BellRing as LBellRing,
  Bike as LBike,
  BookOpen as LBookOpen,
  Bookmark as LBookmark,
  Briefcase as LBriefcase,
  Bug as LBug,
  Building2 as LBuilding2,
  Calendar as LCalendar,
  CalendarCheck as LCalendarCheck,
  CalendarDays as LCalendarDays,
  CalendarX as LCalendarX,
  Camera as LCamera,
  Car as LCar,
  CarFront as LCarFront,
  CarTaxiFront as LCarTaxiFront,
  ChartLine as LChartLine,
  Check as LCheck,
  ChevronDown as LChevronDown,
  ChevronLeft as LChevronLeft,
  ChevronRight as LChevronRight,
  ChevronUp as LChevronUp,
  Circle as LCircle,
  CircleCheck as LCircleCheck,
  CircleHelp as LCircleHelp,
  CirclePlay as LCirclePlay,
  CircleX as LCircleX,
  Clapperboard as LClapperboard,
  Clock as LClock,
  Coffee as LCoffee,
  Compass as LCompass,
  Copy as LCopy,
  CreditCard as LCreditCard,
  DollarSign as LDollarSign,
  Download as LDownload,
  Earth as LEarth,
  EllipsisVertical as LEllipsisVertical,
  ExternalLink as LExternalLink,
  Eye as LEye,
  EyeOff as LEyeOff,
  FileSearch as LFileSearch,
  Flag as LFlag,
  Footprints as LFootprints,
  Frown as LFrown,
  Gauge as LGauge,
  Globe as LGlobe,
  GraduationCap as LGraduationCap,
  GripVertical as LGripVertical,
  Hash as LHash,
  Headset as LHeadset,
  Heart as LHeart,
  House as LHouse,
  IdCard as LIdCard,
  Image as LImage,
  ImageOff as LImageOff,
  ImagePlus as LImagePlus,
  Images as LImages,
  Info as LInfo,
  Landmark as LLandmark,
  Languages as LLanguages,
  LayoutDashboard as LLayoutDashboard,
  Link as LLink,
  ListFilter as LListFilter,
  Locate as LLocate,
  LocateFixed as LLocateFixed,
  Lock as LLock,
  LogOut as LLogOut,
  Mail as LMail,
  Map as LMap,
  MapPin as LMapPin,
  MapPinned as LMapPinned,
  Martini as LMartini,
  MessageCircle as LMessageCircle,
  MessageSquare as LMessageSquare,
  MessagesSquare as LMessagesSquare,
  Minus as LMinus,
  Navigation as LNavigation,
  Paperclip as LPaperclip,
  Pencil as LPencil,
  PersonStanding as LPersonStanding,
  Phone as LPhone,
  Plane as LPlane,
  Plus as LPlus,
  QrCode as LQrCode,
  ReceiptText as LReceiptText,
  RefreshCw as LRefreshCw,
  Repeat as LRepeat,
  Rocket as LRocket,
  Save as LSave,
  ScanQrCode as LScanQrCode,
  Search as LSearch,
  SendHorizontal as LSendHorizontal,
  Settings as LSettings,
  Share2 as LShare2,
  ShieldCheck as LShieldCheck,
  ShoppingBag as LShoppingBag,
  ShoppingCart as LShoppingCart,
  Smartphone as LSmartphone,
  Smile as LSmile,
  SmilePlus as LSmilePlus,
  Square as LSquare,
  SquareUser as LSquareUser,
  Star as LStar,
  Store as LStore,
  Tag as LTag,
  ThumbsUp as LThumbsUp,
  Ticket as LTicket,
  Timer as LTimer,
  Trash2 as LTrash2,
  Trees as LTrees,
  TrendingUp as LTrendingUp,
  Truck as LTruck,
  User as LUser,
  UserPlus as LUserPlus,
  Users as LUsers,
  UsersRound as LUsersRound,
  UtensilsCrossed as LUtensilsCrossed,
  Video as LVideo,
  Wrench as LWrench,
  X as LX,
  Sparkles as LSparkles,
} from "lucide-react";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const STROKE = 1.8;

const make = (Lucide, { filled = false } = {}) => {
  const Icon = forwardRef(({ sx, ...props }, ref) => (
    <SvgIcon
      ref={ref}
      component={Lucide}
      strokeWidth={STROKE}
      // SvgIcon rellena con currentColor; los de Lucide son de trazo
      sx={[!filled && { fill: "none" }, ...(Array.isArray(sx) ? sx : [sx])].filter(Boolean)}
      {...props}
    />
  ));
  Icon.muiName = SvgIcon.muiName;
  return Icon;
};

export const AccessTime = make(LClock);
export const AccountBalance = make(LLandmark);
export const AccountBox = make(LSquareUser);
export const Add = make(LPlus);
export const AddBusiness = make(LStore);
export const AddPhotoAlternate = make(LImagePlus);
export const AddReactionOutlined = make(LSmilePlus);
export const AdminPanelSettings = make(LShieldCheck);
export const AlternateEmail = make(LAtSign);
export const ArrowBack = make(LArrowLeft);
export const ArrowBackIos = make(LChevronLeft);
export const ArrowForward = make(LArrowRight);
export const ArrowForwardIos = make(LChevronRight);
export const AttachFile = make(LPaperclip);
export const AttachMoney = make(LDollarSign);
export const AutoStories = make(LBookOpen);
export const Badge = make(LIdCard);
export const Bookmark = make(LBookmark, { filled: true });
export const BookmarkBorder = make(LBookmark);
export const BugReport = make(LBug);
export const Build = make(LWrench);
export const CalendarMonth = make(LCalendarDays);
export const CameraAlt = make(LCamera);
export const Cancel = make(LCircleX);
export const Chat = make(LMessageSquare);
export const ChatBubbleOutline = make(LMessageCircle);
export const Check = make(LCheck);
export const CheckCircle = make(LCircleCheck);
export const CheckCircleOutline = make(LCircleCheck);
export const ChevronLeft = make(LChevronLeft);
export const ChevronLeftRounded = ChevronLeft;
export const ChevronRight = make(LChevronRight);
export const Circle = make(LCircle, { filled: true });
export const Close = make(LX);
export const ConfirmationNumber = make(LTicket);
export const ContentCopy = make(LCopy);
export const Dashboard = make(LLayoutDashboard);
export const Delete = make(LTrash2);
export const DeleteOutline = Delete;
export const DeliveryDining = make(LBike);
export const DirectionsBike = make(LBike);
export const DirectionsCar = make(LCar);
export const DirectionsCarFilled = make(LCarFront);
export const DirectionsRun = make(LFootprints);
export const DirectionsWalk = make(LPersonStanding);
export const Download = make(LDownload);
export const DragIndicator = make(LGripVertical);
export const Edit = make(LPencil);
export const Email = make(LMail);
export const EmojiEmotions = make(LSmile);
export const Event = make(LCalendar);
export const EventAvailable = make(LCalendarCheck);
export const EventBusy = make(LCalendarX);
export const ExitToApp = make(LLogOut);
export const ExpandLess = make(LChevronUp);
export const ExpandMore = make(LChevronDown);
export const Explore = make(LCompass);
export const Favorite = make(LHeart, { filled: true });
export const FavoriteBorder = make(LHeart);
export const FiberManualRecord = Circle;
export const FilterList = make(LListFilter);
export const Flag = make(LFlag);
export const Flight = make(LPlane);
export const Forum = make(LMessagesSquare);
export const ForumOutlined = Forum;
export const GpsFixed = make(LLocateFixed);
export const GpsNotFixed = make(LLocate);
export const Group = make(LUsers);
export const Groups = make(LUsersRound);
export const Help = make(LCircleHelp);
export const Home = make(LHouse);
export const HomeWork = make(LBuilding2);
export const Image = make(LImage);
export const ImageNotSupported = make(LImageOff);
export const Info = make(LInfo);
export const InsertEmoticon = EmojiEmotions;
export const KeyboardArrowDownRounded = ExpandMore;
export const Language = make(LGlobe);
export const Link = make(LLink);
export const LocalBar = make(LMartini);
export const LocalCafe = make(LCoffee);
export const LocalOffer = make(LTag);
export const LocalShipping = make(LTruck);
export const LocationOn = make(LMapPin);
export const Lock = make(LLock);
export const MailOutline = Email;
export const Map = make(LMap);
export const MoodOutlined = EmojiEmotions;
export const MoreVert = make(LEllipsisVertical);
export const MyLocation = GpsFixed;
export const Navigation = make(LNavigation);
export const Notifications = make(LBell);
export const NotificationAddOutlined = make(LBellPlus);
export const NotificationsActive = make(LBellRing, { filled: true });
export const OpenInNew = make(LExternalLink);
export const Park = make(LTrees);
export const Payment = make(LCreditCard);
export const Person = make(LUser);
export const PersonAdd = make(LUserPlus);
export const PersonAddAlt1 = PersonAdd;
export const Phone = make(LPhone);
export const PhoneIphone = make(LSmartphone);
export const PhotoCamera = CameraAlt;
export const PhotoLibrary = make(LImages);
export const PlayCircle = make(LCirclePlay);
export const Policy = make(LFileSearch);
export const QrCode2 = make(LQrCode);
export const QrCodeScanner = make(LScanQrCode);
export const QueryStats = make(LChartLine);
export const ReceiptLong = make(LReceiptText);
export const Refresh = make(LRefreshCw);
export const Remove = make(LMinus);
export const Repeat = make(LRepeat);
export const Restaurant = make(LUtensilsCrossed);
export const RocketLaunch = make(LRocket);
export const Save = make(LSave);
export const Schedule = AccessTime;
export const School = make(LGraduationCap);
export const Search = make(LSearch);
export const Send = make(LSendHorizontal);
export const SentimentDissatisfied = make(LFrown);
export const Settings = make(LSettings);
export const Share = make(LShare2);
export const ShareLocation = make(LMapPinned);
export const ShoppingBag = make(LShoppingBag);
export const ShoppingCart = make(LShoppingCart);
export const ShowChart = make(LTrendingUp);
export const Speed = make(LGauge);
export const Star = make(LStar, { filled: true });
export const StarBorder = make(LStar);
export const Stop = make(LSquare, { filled: true });
export const Store = make(LStore);
export const Storefront = Store;
export const StorefrontRounded = Store;
export const SupportAgent = make(LHeadset);
export const AutoAwesome = make(LSparkles);
export const Tag = make(LHash);
export const ThumbUpAlt = make(LThumbsUp);
export const TimeToLeave = make(LCarTaxiFront);
export const Timeline = make(LActivity);
export const TimerOutlined = make(LTimer);
export const Translate = make(LLanguages);
export const TravelExplore = make(LEarth);
export const Verified = make(LBadgeCheck);
export const VideoCall = make(LVideo);
export const VideoLibrary = make(LClapperboard);
export const Visibility = make(LEye);
export const VisibilityOff = make(LEyeOff);
export const WhatsApp = WhatsAppIcon; // marca: se mantiene el logo original
export const Work = make(LBriefcase);
